/**
 * /app/pos/caja — POS: pantalla de cobro touch-first.
 *
 * Layout tablet landscape:
 *   Columna Izquierda (60%): buscador + botón scanner + tabs categorías + grilla de productos
 *   Columna Derecha (40%): ticket activo + totales + botón Cobrar
 *
 * Pago: modal con selector de método + numpad virtual + vuelto + split + fidelización/giftcard.
 * Scanner: keyboard-wedge global (HID) + Search Enter + cámara; lookup EAN/SKU (SCRUM-203).
 * Responsive portrait: carrito en bottom-sheet deslizable.
 *
 * Modo "añadir a pedido": ?addToOrder=<orderId> — la grilla suma ítems a una orden
 * existente (POST /api/orders/items) en vez de crear checkout nuevo.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState, useCallback, useMemo, type RefObject } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { PosCajaTour } from "@/components/ProductTour";
import { PageLoader } from "@/components/PageLoader";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import { Button } from "@/components/ui/button";
import {
  Search,
  ScanBarcode,
  Camera,
  X,
  Plus,
  Minus,
  Trash2,
  CreditCard,
  Banknote,
  QrCode,
  Gift,
  Star,
  ChevronUp,
  ShoppingCart,
  ArrowLeft,
  CheckCircle2,
  Wallet,
  ClipboardList,
  PackagePlus,
  LayoutGrid,
  List,
  Pencil,
  Tag,
} from "lucide-react";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { tryHipRoleAction } from "@/lib/hardware/client";
import { PosPagoModal } from "@/components/pos/PosPagoModal";
import { PosSaleInvoicePanel } from "@/components/pos/PosSaleInvoicePanel";
import { PosEditProductDialog } from "@/components/pos/PosEditProductDialog";
import {
  PosTicketCustomerBar,
  type PosTicketCustomer,
} from "@/components/pos/PosTicketCustomerBar";
import { PosConnectivityBanner } from "@/components/pos/PosConnectivityBanner";
import {
  canQueueOfflineSale,
  countPendingOfflineSales,
  enqueueOfflineSale,
  getNavigatorOnline,
  isCatalogSnapshotFresh,
  loadPosCatalogSnapshot,
  newClientSaleId,
  savePosCatalogSnapshot,
  syncOfflinePosSales,
  usePosOnlineStatus,
  type PosOfflineOutboxSale,
} from "@/lib/pos/offline";
import { CustomerDisplayPairPanel } from "@/components/pos/CustomerDisplayPairPanel";
import { useTenantContext } from "@/lib/tenant-context";
import { buildPriceListItemMap, resolvePriceListOverride, type OnlinePriceListItem } from "@/lib/storefront-online-price-list";
import { roundPriceFields, type PriceRoundingMode } from "@/lib/price-rounding";
import type { OrderStatus } from "@/domain/sm-models";
import type { PosMetodoPago } from "@/lib/pos-payment-methods";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { toast } from "sonner";
import { applySaleQtyDelta, formatSaleQty, getSaleQtyRules, initialSaleQty } from "@/lib/sale-qty-rules";
import { looksLikeScanCode, resolvePosScanCode } from "@/lib/pos/barcode-resolve";
import { formatPosProductMetaLine } from "@/lib/pos/product-meta-line";
import {
  defaultPosInvoiceMode,
  resolvePosInvoicingPolicy,
  type PosInvoiceMode,
  type PosInvoicingPolicy,
} from "@/lib/pos/invoicing-policy";
import {
  KeyboardWedgeBuffer,
  isBlockingOverlayOpen,
  isManualEditableTarget,
} from "@/lib/pos/keyboard-wedge";
import { canAddItemsToOrder, shortOrderId } from "@/lib/order-add-items";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useQz } from "@/lib/printing/use-qz";
import { getPrinterForRole, paperWidthChars } from "@/lib/printing/printer-service";
import { buildReceiptEscPos } from "@/lib/printing/escpos-builder";
import { TaxBreakdown, type TaxBreakdownTotals } from "@/components/tax/TaxBreakdown";
import { useOperationalMode, type OperationalShortcut } from "@/hooks/use-operational-mode";
import { readPosCart, writePosCart } from "@/lib/pos-cart-storage";
import { OperationalModeShell, OperationalModeToggle } from "@/components/operational-mode/OperationalModeShell";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OPERATIONAL_HOST_LAYOUT } from "@/lib/operational-mode-classes";
import { calculatePosLocalTaxTotals } from "@/lib/pos/local-tax-quote";

export const Route = createFileRoute("/app/pos/caja")({
  component: PosCajaPage,
  validateSearch: (s: Record<string, unknown>) => ({
    addToOrder:
      typeof s.addToOrder === "string" && s.addToOrder.trim().length > 0
        ? s.addToOrder.trim()
        : undefined,
  }),
  head: () => ({ meta: [{ title: "Caja POS" }] }),
});

const POS_VIEW_STORAGE_KEY = "sm.pos.productView";
type PosProductView = "tarjetas" | "lista";

function loadPosProductView(): PosProductView {
  if (typeof window === "undefined") return "tarjetas";
  return window.localStorage.getItem(POS_VIEW_STORAGE_KEY) === "lista" ? "lista" : "tarjetas";
}

const POS_ALPHABET = "ABCDEFGHIJKLMNÑOPQRSTUVWXYZ".split("");

function measurePosInteraction(name: "pos-add-item" | "pos-update-qty" | "pos-remove-item"): void {
  if (typeof window === "undefined" || !window.performance?.mark) return;
  const stamp = String(Date.now());
  const start = name + "-start-" + stamp;
  const end = name + "-end-" + stamp;
  performance.mark(start);
  window.requestAnimationFrame(() => {
    performance.mark(end);
    try {
      performance.measure(name, start, end);
    } catch {
      /* Best-effort telemetry; never affect POS UX. */
    }
    performance.clearMarks(start);
    performance.clearMarks(end);
  });
}

/** Primera letra para agrupar/ordenar (sin acentos, mayúscula; Ñ se conserva). */
function posFirstLetter(nombre: string): string {
  const upper = nombre.trim().toUpperCase();
  const ch = upper.charAt(0);
  if (ch === "Ñ") return ch;
  const clean = ch.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return /^[A-Z]$/.test(clean) ? clean : "#";
}

// ─── TIPOS ────────────────────────────────────────────────────────────────────

interface Producto {
  id: string;
  nombre: string;
  sku: string | null;
  /** SCRUM-203: EAN/GTIN del producto (lookup scanner). */
  ean: string | null;
  precioUnitario: number | null;
  precioPorKg: number | null;
  tipoVenta: "unidad" | "peso";
  unidadMedida: string;
  minQty: number | null;
  qtyStep: number | null;
  imagenUrl: string | null;
  categoriaId: string | null;
  categoriaName?: string;
  /** SCRUM-120: vendible en la sucursal de la sesión (null = aún no resuelto). */
  sellableAtLocation?: boolean | null;
  /** SCRUM-234: stock en la sucursal de la sesión (null = sin dato / on-demand). */
  locationStock?: number | null;
  /** SCRUM-234: si el producto trackea inventario. */
  trackStock?: boolean | null;
  /** SCRUM-234: estado de disponibilidad (in_stock / low_stock / out_of_stock / …). */
  stockStatus?: string | null;
  /** Fiscal metadata preloaded with the product for the transient POS quote. */
  priceMode?: "net" | "final";
  taxCategory?: "taxable" | "exempt" | "non_taxable" | "not_subject";
  taxRate?: number | null;
}

interface Categoria {
  id: string;
  nombre: string;
}

interface CartItem {
  productId: string;
  nombre: string;
  precio: number;
  qty: number;
  imagenUrl: string | null;
  tipoVenta: "unidad" | "peso";
  unidadMedida: string;
  minQty: number | null;
  qtyStep: number | null;
  variantId?: string | null;
  variantName?: string | null;
  ean?: string | null;
  priceMode?: "net" | "final";
  taxCategory?: "taxable" | "exempt" | "non_taxable" | "not_subject";
  taxRate?: number | null;
}

/** Orden destino del modo "añadir a pedido". */
interface AddTargetOrder {
  id: string;
  customerName: string;
  status: string;
  precioEstimado: number;
  precioFinal?: number | null;
  amountPaid?: number | null;
}

type MetodoPago = PosMetodoPago;

const pesos = (n: number) =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(n);

/** SCRUM-234 — línea secundaria: stock + código (EAN preferido, fallback SKU). */
function posProductMetaLine(p: Producto): string {
  return formatPosProductMetaLine(p);
}

// ─── NUMPAD VIRTUAL ───────────────────────────────────────────────────────────

function NumPad({
  value,
  onChange,
  onConfirm,
}: {
  value: string;
  onChange: (v: string) => void;
  onConfirm?: () => void;
}) {
  const press = (k: string) => {
    if (k === "⌫") {
      onChange(value.slice(0, -1) || "0");
      return;
    }
    if (k === "C") {
      onChange("0");
      return;
    }
    if (k === "OK") {
      onConfirm?.();
      return;
    }
    if (k === "." && value.includes(".")) return;
    const next = value === "0" && k !== "." ? k : value + k;
    onChange(next);
  };
  const keys = ["7", "8", "9", "C", "⌫", "4", "5", "6", "0", "00", "1", "2", "3", ".", "OK"];
  return (
    <div className="grid grid-cols-5 gap-1.5">
      {keys.map((k) => (
        <button
          key={k}
          onClick={() => press(k)}
          className={`flex h-12 min-w-[48px] items-center justify-center rounded-xl border text-sm font-semibold transition-colors
            ${
              k === "OK"
                ? "col-span-1 border-primary/60 bg-primary/20 text-primary hover:bg-primary/30"
                : k === "C" || k === "⌫"
                  ? "border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20"
                  : "border-border/60 bg-card hover:bg-muted"
            }`}
        >
          {k}
        </button>
      ))}
    </div>
  );
}

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────

function PosCajaPage() {
  const tid = useWorkspaceTenantId();
  const { productFlags } = useTenantContext();
  const { addToOrder: addToOrderId } = Route.useSearch();
  const navigate = useNavigate({ from: "/app/pos/caja" });
  const { isReady: qzReady, connect: qzConnect, print: qzPrint } = useQz();
  const [printing, setPrinting] = useState(false);
  const addMode = Boolean(addToOrderId);
  const [addTarget, setAddTarget] = useState<AddTargetOrder | null>(null);
  const [addTargetLoading, setAddTargetLoading] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  const [pickLoading, setPickLoading] = useState(false);
  const [pickOrders, setPickOrders] = useState<AddTargetOrder[]>([]);

  // Session
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionLocationId, setSessionLocationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);

  // Catálogo
  const [productos, setProductos] = useState<Producto[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [catActiva, setCatActiva] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [vista, setVista] = useState<PosProductView>(loadPosProductView);

  // ── SCRUM-405: lista de precios de la venta — el stock es independiente,
  // solo cambian precio/disponibilidad de artículos de esa lista. rawCatalogRef
  // guarda el catálogo base (precio directo, sin lista ni redondeo) para poder
  // recalcular al cambiar de lista sin volver a pedir /api/products.
  const rawCatalogRef = useRef<Array<Record<string, unknown>>>([]);
  const priceRoundingRef = useRef<PriceRoundingMode | undefined>(undefined);
  const [priceListOptions, setPriceListOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedPriceListId, setSelectedPriceListId] = useState<string | null>(null);

  useEffect(() => {
    window.localStorage.setItem(POS_VIEW_STORAGE_KEY, vista);
  }, [vista]);

  // Cart
  const [cart, setCart] = useState<CartItem[]>([]);
  const cartHydratedRef = useRef(false);

  // SCRUM-82: recuperación de contexto — una venta en curso no se pierde si
  // se refresca la página o se corta la red. Se restaura una sola vez, y
  // solo si el cajero no llegó a tocar nada durante la carga de la sesión
  // (no pisa un carrito recién empezado).
  useEffect(() => {
    if (!tid || !sessionId || cartHydratedRef.current) return;
    cartHydratedRef.current = true;
    if (cart.length > 0) return;
    const stored = readPosCart(tid, sessionId, addToOrderId ?? null);
    if (stored.length) {
      setCart(stored as CartItem[]);
      toast.message("Recuperamos una venta que quedó en curso");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tid, sessionId, addToOrderId]);

  useEffect(() => {
    if (!tid || !sessionId || !cartHydratedRef.current) return;
    writePosCart(tid, sessionId, addToOrderId ?? null, cart);
  }, [tid, sessionId, addToOrderId, cart]);


  // SCRUM-120: disponibilidad por sucursal de la sesión de caja (no stock global).
  // SCRUM-398: /api/pos/stock-availability rechaza (400) más de 200 ids por
  // request — desde que SCRUM-241(?) sacó el ?limit=200 de /api/products para
  // que catálogos grandes (347 productos en t-frosty) no quedaran invisibles,
  // esta llamada empezó a superar ese tope y el .catch silencioso dejaba el
  // stock sin mostrar para TODO el catálogo. Se chunkea en lotes de 200.
  useEffect(() => {
    if (!tid || !sessionLocationId || productos.length === 0) return;
    let cancelled = false;
    const STOCK_AVAILABILITY_BATCH_SIZE = 200;
    const ids = productos.map((p) => p.id);
    const batches: string[][] = [];
    for (let i = 0; i < ids.length; i += STOCK_AVAILABILITY_BATCH_SIZE) {
      batches.push(ids.slice(i, i + STOCK_AVAILABILITY_BATCH_SIZE));
    }

    void (async () => {
      const merged: Record<
        string,
        { sellable?: boolean; locationStock?: number | null; status?: string; trackStock?: boolean }
      > = {};
      for (const batch of batches) {
        if (cancelled) return;
        try {
          const r = await apiSendJson<{
            products?: Record<
              string,
              { sellable?: boolean; locationStock?: number | null; status?: string; trackStock?: boolean }
            >;
          }>("/api/pos/stock-availability", "POST", { productIds: batch, locationId: sessionLocationId }, tid);
          Object.assign(merged, r.products ?? {});
        } catch (e) {
          console.error("[pos.caja] stock-availability batch failed", e);
        }
      }
      if (cancelled) return;
      setProductos((prev) =>
        prev.map((p) => {
          const a = merged[p.id];
          if (!a) return { ...p, sellableAtLocation: true };
          return {
            ...p,
            sellableAtLocation: a.sellable !== false,
            locationStock: a.locationStock ?? null,
            trackStock: a.trackStock ?? null,
            stockStatus: a.status ?? null,
          };
        }),
      );
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo re-consultar al cambiar sesión/sucursal
  }, [tid, sessionLocationId, productos.length]);

  // SCRUM-507: the active cart is transient UI state. Fiscal totals are derived
  // locally from fiscal metadata already loaded with each product; checkout
  // remains the authoritative server-side validation boundary.
  const taxTotals = useMemo(
    () =>
      calculatePosLocalTaxTotals(
        cart.map((item) => ({
          tipoVenta: item.tipoVenta,
          precio: item.precio,
          cantidad: item.qty,
          priceMode: item.priceMode,
          taxCategory: item.taxCategory,
          taxRate: item.taxRate,
        })),
      ),
    [cart],
  );

  const [cartOpen, setCartOpen] = useState(false); // mobile portrait
  // SCRUM-235: edición de producto desde ticket + highlight
  const [editProduct, setEditProduct] = useState<CartItem | null>(null);
  const [highlightProductId, setHighlightProductId] = useState<string | null>(null);
  const ticketScrollRef = useRef<HTMLDivElement | null>(null);

  // Scanner
  const [scanMode, setScanMode] = useState<"none" | "keyboard" | "camera">("none");
  const [scanBuffer, setScanBuffer] = useState("");
  const scanRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Hardware (HIP) — cajón / escáner / terminal / impresora vía bindings.
  // Sin asignación → modo manual (teclado, QZ, confirmación a mano).
  const [cashDrawerBound, setCashDrawerBound] = useState(false);
  const [paymentTerminalBound, setPaymentTerminalBound] = useState(false);
  const [openingDrawer, setOpeningDrawer] = useState(false);
  const [hipScanning, setHipScanning] = useState(false);

  // Pago
  const [showPago, setShowPago] = useState(false);
  const [invoicingPolicy, setInvoicingPolicy] = useState<PosInvoicingPolicy>("optional");
  const [invoiceMode, setInvoiceMode] = useState<PosInvoiceMode>("none");
  const [ticketCustomer, setTicketCustomer] = useState<PosTicketCustomer | null>(null);
  const online = usePosOnlineStatus();
  // SCRUM-239: capability central, no política suelta del tenant.
  const offlineCapabilityEnabled = productFlags.payment_pos_offline === true;
  // SCRUM-340: sin proveedor de facturación habilitado no hay nada que ofrecer
  // después del cobro; el modal muestra solo imprimir y nueva venta.
  const invoicingEnabled =
    productFlags.invoicing_afip_direct === true || productFlags.invoicing_tusfacturas === true;
  const [pendingOfflineSales, setPendingOfflineSales] = useState(0);
  const [syncingOffline, setSyncingOffline] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [lastSale, setLastSale] = useState<{
    orderId: string;
    orderNumber?: number | null;
    total: number;
    metodo: MetodoPago;
    vuelto: number;
    items: CartItem[];
    invoiceSummary?: string | null;
    /** Para prellenar el envío del comprobante por mail (SCRUM-340). */
    customerEmail?: string | null;
    customerName?: string | null;
  } | null>(null);
  /** SCRUM-364: datos reales del negocio para el ticket impreso (título + pie). */
  const [businessInfo, setBusinessInfo] = useState<{
    name: string;
    address: string | null;
    phone: string | null;
    whatsapp: string | null;
    website: string | null;
  }>({ name: "Structura POS", address: null, phone: null, whatsapp: null, website: null });

  // ── Inicialización ──
  useEffect(() => {
    if (!tid) return;
    const init = async () => {
      try {
        const [sr, pr, cr, cfgRes, tenantRes, plRes, hardwareRes] = await Promise.all([
          apiGetJson<{ session?: { id: string; locationId?: string | null }; _lock?: PlanApiErrorPayload }>(`/api/pos/session`, tid),
          // Sin `limit`: dbSmListProducts devuelve el catálogo completo, sin tope de 200 —
          // necesario porque el buscador y el scan de código de barras del POS resuelven
          // contra este snapshot en memoria (ver pos/barcode-resolve.ts). Con el límite
          // fijo de antes, cualquier producto fuera de las primeras 200 filas (por
          // sort_order/nombre) era invisible para el cajero, tanto buscando como escaneando.
          apiGetJson<Array<Record<string, unknown>> | { items?: Array<Record<string, unknown>>; products?: Array<Record<string, unknown>> }>(`/api/products`, tid),
          apiGetJson<Array<Record<string, unknown>> | { categories?: Array<Record<string, unknown>> }>(`/api/categories`, tid),
          apiGetJson<{ config?: { storePolicies?: Record<string, unknown>; whatsappNumber?: string | null; priceRounding?: PriceRoundingMode } | null }>(`/api/tenant/config`, tid).catch(() => ({ config: null })),
          apiGetJson<{ tenant?: { name?: string; phone?: string | null; address?: string | null; socialLinks?: Record<string, string> } }>(`/api/tenant`, tid).catch(() => ({ tenant: undefined })),
          productFlags?.price_lists
            ? apiGetJson<{ enabled: boolean; onlineListId: string | null; posListId: string | null; lists?: Array<{ id: string; name?: string; nombre?: string }> }>(`/api/products/price-lists`, tid).catch(() => ({ enabled: false, onlineListId: null, posListId: null, lists: [] }))
            : Promise.resolve({ enabled: false, onlineListId: null, posListId: null, lists: [] }),
          apiGetJson<{ bindings?: { cashDrawer?: string; paymentTerminal?: string } }>(`/api/hardware/bindings`, tid).catch(() => ({ bindings: undefined })),
        ]);
        if (sr._lock) {
          setLock(sr._lock);
          return;
        }
        if (tenantRes.tenant) {
          setBusinessInfo({
            name: tenantRes.tenant.name?.trim() || "Structura POS",
            address: tenantRes.tenant.address ?? null,
            phone: tenantRes.tenant.phone ?? null,
            whatsapp: cfgRes.config?.whatsappNumber ?? null,
            website: tenantRes.tenant.socialLinks?.website ?? null,
          });
        }
        setCashDrawerBound(Boolean(hardwareRes.bindings?.cashDrawer));
        setPaymentTerminalBound(Boolean(hardwareRes.bindings?.paymentTerminal));
        setSessionId(sr.session?.id ?? null);
        setSessionLocationId(sr.session?.locationId ?? null);
        const policy = resolvePosInvoicingPolicy(cfgRes.config?.storePolicies ?? {});
        setInvoicingPolicy(policy);
        setInvoiceMode(defaultPosInvoiceMode(policy));
        let rawProducts = Array.isArray(pr) ? pr : (pr.items ?? pr.products ?? []);
        // SCRUM-405: catálogo base (precio directo, sin lista ni redondeo) —
        // se guarda para poder recalcular al cambiar de lista de precios sin
        // volver a pedir /api/products. El stock (no está acá) es del producto,
        // no de la lista, así que no hace falta re-resolverlo nunca.
        rawCatalogRef.current = rawProducts;

        // El POS resuelve precio como la tienda online por defecto (misma lista fijada),
        // salvo que el tenant haya fijado una lista propia para mostrador — ver
        // resolveActivePosPriceListId. Sin price_lists habilitado, esto es un no-op.
        // SCRUM-509: price-list configuration is independent of catalog/session data,
        // so it is fetched in parallel with the POS bootstrap above.
        const activeListId = plRes.enabled ? plRes.posListId ?? plRes.onlineListId ?? null : null;
        if (plRes.enabled) {
          setPriceListOptions(
            (plRes.lists ?? []).map((l) => ({ id: l.id, name: l.name ?? l.nombre ?? "Lista sin nombre" })),
          );
        }
        setSelectedPriceListId(activeListId);
        if (activeListId) {
          try {
            const list = await apiGetJson<{ items?: OnlinePriceListItem[] }>(`/api/b2b/channels?id=${activeListId}`, tid);
            const byProduct = buildPriceListItemMap(list.items ?? []);
            if (byProduct.size > 0) {
              // SCRUM-429: la lista de precios del POS restringe el catálogo mostrado
              // a los productos que tienen item en esa lista — antes era solo overlay
              // de precio sobre el catálogo completo (SCRUM-405/351).
              rawProducts = rawProducts
                .filter((x) => byProduct.has(String(x.id)))
                .map((x) => {
                  const override = resolvePriceListOverride(byProduct, String(x.id), String(x.tipoVenta ?? "unidad"), {
                    precioUnitario: (x.precioUnitario as number | null) ?? null,
                    precioPorKg: (x.precioPorKg as number | null) ?? null,
                  });
                  return { ...x, ...override };
                });
            }
          } catch {
            /* fallo aislado — POS sigue con el precio directo del producto */
          }
        }
        // SCRUM-397: redondeo general de precios (config del Negocio) — nunca
        // se muestra ni se cobra el precio sin redondear en Caja.
        const posPriceRounding = cfgRes.config?.priceRounding;
        priceRoundingRef.current = posPriceRounding;
        if (posPriceRounding && posPriceRounding !== "none") {
          rawProducts = rawProducts.map((x) => ({
            ...x,
            ...roundPriceFields(
              {
                precioUnitario: (x.precioUnitario as number | null) ?? null,
                precioPorKg: (x.precioPorKg as number | null) ?? null,
              },
              posPriceRounding,
            ),
          }));
        }
        setProductos(rawProducts.map((x) => ({
          id: String(x.id),
          nombre: String(x.name ?? x.nombre ?? ""),
          sku: (x.sku as string | null) ?? null,
          ean: (x.ean as string | null) ?? null,
          precioUnitario: (x.precioUnitario as number | null) ?? null,
          precioPorKg: (x.precioPorKg as number | null) ?? null,
          tipoVenta: ((x.tipoVenta as string) === "peso" ? "peso" : "unidad") as "unidad" | "peso",
          unidadMedida: String(x.unidadMedida ?? "kg"),
          minQty: x.minQty != null ? Number(x.minQty) : null,
          qtyStep: x.qtyStep != null ? Number(x.qtyStep) : null,
          imagenUrl: (x.imagenUrl as string | null) ?? null,
          categoriaId: (x.categoriaId as string | null) ?? null,
          categoriaName: x.categoriaName as string | undefined,
          priceMode: x.priceMode === "net" ? "net" : "final",
          taxCategory: (["taxable", "exempt", "non_taxable", "not_subject"] as const).includes(x.taxCategory as any)
            ? (x.taxCategory as "taxable" | "exempt" | "non_taxable" | "not_subject")
            : "taxable",
          taxRate: x.taxRate != null ? Number(x.taxRate) : null,
        })));
        const rawCats = Array.isArray(cr) ? cr : (cr.categories ?? []);
        setCategorias(rawCats.map((x) => ({
          id: String(x.id),
          nombre: String(x.nombre ?? x.name ?? ""),
        })));
        // SCRUM-239: snapshot local solo si la capability offline está habilitada.
        if (offlineCapabilityEnabled) {
          void savePosCatalogSnapshot({
            tenantId: tid,
            locationId: sr.session?.locationId ?? null,
            savedAt: new Date().toISOString(),
            products: rawProducts.map((x) => ({
              id: String(x.id),
              nombre: String(x.name ?? x.nombre ?? ""),
              sku: (x.sku as string | null) ?? null,
              ean: (x.ean as string | null) ?? null,
              precioUnitario: (x.precioUnitario as number | null) ?? null,
              precioPorKg: (x.precioPorKg as number | null) ?? null,
              tipoVenta: ((x.tipoVenta as string) === "peso" ? "peso" : "unidad") as "unidad" | "peso",
              unidadMedida: String(x.unidadMedida ?? "kg"),
              minQty: x.minQty != null ? Number(x.minQty) : null,
              qtyStep: x.qtyStep != null ? Number(x.qtyStep) : null,
              imagenUrl: (x.imagenUrl as string | null) ?? null,
              categoriaId: (x.categoriaId as string | null) ?? null,
              categoriaName: x.categoriaName as string | undefined,
              priceMode: x.priceMode === "net" ? "net" : "final",
              taxCategory: (["taxable", "exempt", "non_taxable", "not_subject"] as const).includes(x.taxCategory as any)
                ? (x.taxCategory as "taxable" | "exempt" | "non_taxable" | "not_subject")
                : "taxable",
              taxRate: x.taxRate != null ? Number(x.taxRate) : null,
            })),
            categories: rawCats.map((x) => ({
              id: String(x.id),
              nombre: String(x.nombre ?? x.name ?? ""),
            })),
          }).catch(() => undefined);
          void countPendingOfflineSales(tid).then(setPendingOfflineSales).catch(() => undefined);
        }
      } catch {
        try {
          const snap = offlineCapabilityEnabled ? await loadPosCatalogSnapshot(tid) : null;
          if (snap && isCatalogSnapshotFresh(snap) && snap.products.length) {
            setProductos(snap.products);
            setCategorias(snap.categories);
            toast.message("Modo offline — catálogo local");
          } else {
            toast.error("Error al cargar datos");
          }
        } catch {
          toast.error("Error al cargar datos");
        }
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [tid, offlineCapabilityEnabled]);

  // SCRUM-509: hardware bindings are bootstrapped with the POS session/catalog
  // instead of adding a second waterfall after first paint.

  const openCashDrawer = async () => {
    if (!tid) return;
    setOpeningDrawer(true);
    try {
      const r = await tryHipRoleAction(tid, "cashDrawer", "openDrawer");
      if (r.mode === "hardware") toast.success("Cajón abierto");
      else toast.message("Sin cajón asignado — abrilo manualmente");
    } finally {
      setOpeningDrawer(false);
    }
  };

  const hipScanToCart = async () => {
    if (!tid) return;
    setHipScanning(true);
    try {
      const r = await tryHipRoleAction(tid, "posScanner", "scan");
      if (r.mode !== "hardware") {
        toast.message("Sin escáner HIP — usá pistola/cámara o buscá a mano");
        return;
      }
      const code = String((r.result as { code?: string } | undefined)?.code ?? "").trim();
      if (!code) {
        toast.error("Escáner sin lectura");
        return;
      }
      applyScannedCode(code);
    } finally {
      setHipScanning(false);
    }
  };

  // ── Scanner teclado (pistola física) ──
  useEffect(() => {
    if (scanMode !== "keyboard") return;
    scanRef.current?.focus();
  }, [scanMode]);

  const handleScanInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const code = scanBuffer.trim();
      setScanBuffer("");
      setScanMode("none");
      if (!code) return;
      applyScannedCode(code);
    }
  };

  // ── Scanner cámara (BarcodeDetector / @zxing fallback) ──
  const startCamera = useCallback(async () => {
    setScanMode("camera");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      if (cameraRef.current) cameraRef.current.srcObject = stream;

      // Intentar BarcodeDetector nativo (Chrome 83+, Android)
      if ("BarcodeDetector" in window) {
        // @ts-expect-error — BarcodeDetector no está en los tipos estándar TS aún
        const detector = new BarcodeDetector({
          formats: ["ean_13", "upc_a", "upc_e", "code_128", "code_39"],
        });
        const scan = async () => {
          if (scanMode !== "camera" || !cameraRef.current) return;
          try {
            const codes = await detector.detect(cameraRef.current);
            if (codes.length > 0) {
              const rawValue = codes[0].rawValue as string;
              stopCamera();
              applyScannedCode(rawValue);
              return;
            }
          } catch {
            /* noop */
          }
          requestAnimationFrame(scan);
        };
        requestAnimationFrame(scan);
      } else {
        toast.info("Tu dispositivo no soporta escaneo por cámara. Usá la pistola física o buscá por nombre.");
        stopCamera();
      }
    } catch {
      toast.error("No se pudo acceder a la cámara. Verificá los permisos.");
      setScanMode("none");
    }
  }, [productos, scanMode]);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setScanMode("none");
  };

  // ── Carrito ──
  const unitPrice = (p: Producto) =>
    p.tipoVenta === "peso" ? (p.precioPorKg ?? 0) : (p.precioUnitario ?? 0);

  const addToCart = (
    p: Producto,
    opts?: { variantId?: string | null; variantName?: string | null },
  ) => {
    measurePosInteraction("pos-add-item");
    if (p.sellableAtLocation === false) {
      toast.error("Sin stock en esta sucursal");
      return;
    }
    const precio = unitPrice(p);
    if (!precio || precio <= 0) {
      toast.error("Este producto no tiene precio");
      return;
    }
    const variantId = opts?.variantId ?? null;
    const variantName = opts?.variantName ?? null;
    setCart((prev) => {
      const idx = prev.findIndex(
        (i) => i.productId === p.id && (i.variantId ?? null) === variantId,
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: applySaleQtyDelta(next[idx].qty, 1, p) };
        return next;
      }
      return [
        ...prev,
        {
          productId: p.id,
          nombre: variantName ? `${p.nombre} — ${variantName}` : p.nombre,
          precio,
          qty: initialSaleQty(p),
          imagenUrl: p.imagenUrl,
          tipoVenta: p.tipoVenta,
          unidadMedida: p.unidadMedida,
          minQty: p.minQty,
          qtyStep: p.qtyStep,
          variantId,
          variantName,
          ean: p.ean,
          priceMode: p.priceMode,
          taxCategory: p.taxCategory,
          taxRate: p.taxRate,
        },
      ];
    });
    setHighlightProductId(p.id);
    window.setTimeout(() => {
      ticketScrollRef.current
        ?.querySelector(`[data-ticket-product-id="${CSS.escape(p.id)}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 50);
    window.setTimeout(() => setHighlightProductId((cur) => (cur === p.id ? null : cur)), 1800);
  };

  /** SCRUM-203: lookup EAN → SKU → variante; feedback claro. */
  const applyScannedCode = useCallback(
    (raw: string) => {
      const resolved = resolvePosScanCode(productos, raw);
      if (!resolved.ok) {
        if (resolved.reason === "empty") return;
        if (resolved.reason === "ambiguous") {
          toast.error(`Código ambiguo (${resolved.count}): ${resolved.code}`);
          return;
        }
        toast.error(`Código no encontrado: ${resolved.code}`);
        return;
      }
      const { hit } = resolved;
      if (hit.kind === "variant") {
        addToCart(hit.product as Producto, {
          variantId: hit.variantId,
          variantName: hit.variantName,
        });
        toast.success(`Escaneado: ${hit.product.nombre}${hit.variantName ? ` — ${hit.variantName}` : ""}`);
      } else {
        addToCart(hit.product as Producto);
        toast.success(`Escaneado: ${hit.product.nombre}`);
      }
      setSearch("");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- addToCart usa state setters estables
    [productos],
  );

  // SCRUM-203: wedge global (pistola HID) sin modo especial obligatorio.
  useEffect(() => {
    const wedge = new KeyboardWedgeBuffer();
    const onKeyDown = (e: KeyboardEvent) => {
      if (showPago || pickOpen || scanMode === "camera") return;
      if (isBlockingOverlayOpen()) return;

      const t = e.target;
      if (t instanceof HTMLElement && t.dataset?.posScanCapture === "search") {
        if (e.key === "Enter") {
          const code = (t as HTMLInputElement).value.trim();
          if (looksLikeScanCode(code)) {
            e.preventDefault();
            applyScannedCode(code);
          }
        }
        const commit = wedge.pushKey(e);
        if (commit) {
          e.preventDefault();
          applyScannedCode(commit.code);
        }
        return;
      }

      if (isManualEditableTarget(e.target)) {
        wedge.reset();
        return;
      }

      const commit = wedge.pushKey(e);
      if (commit) {
        e.preventDefault();
        applyScannedCode(commit.code);
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      wedge.reset();
    };
  }, [applyScannedCode, showPago, pickOpen, scanMode]);

  // ── SCRUM-405/429: cambiar la lista de precios de la venta en curso ──
  // Recalcula SIEMPRE desde rawCatalogRef (precio directo del producto), no
  // desde `productos` actual — evitar acumular redondeos/overrides de la
  // lista anterior. El stock (sellableAtLocation/locationStock/trackStock/
  // stockStatus) queda intacto: no depende de la lista de precios.
  // SCRUM-429: cuando la lista tiene items, el catálogo visible se restringe
  // a esos productos (antes era solo overlay de precio sobre el catálogo
  // completo). Sin lista, o si la carga falla, se muestra el catálogo
  // completo con precio directo (comportamiento previo, sin cambios).
  const applyPriceListSelection = useCallback(
    async (listId: string | null) => {
      if (!tid) return;
      setSelectedPriceListId(listId);
      let rows = rawCatalogRef.current;
      if (listId) {
        try {
          const list = await apiGetJson<{ items?: OnlinePriceListItem[] }>(`/api/b2b/channels?id=${listId}`, tid);
          const byProduct = buildPriceListItemMap(list.items ?? []);
          if (byProduct.size > 0) {
            rows = rows
              .filter((x) => byProduct.has(String(x.id)))
              .map((x) => {
                const override = resolvePriceListOverride(byProduct, String(x.id), String(x.tipoVenta ?? "unidad"), {
                  precioUnitario: (x.precioUnitario as number | null) ?? null,
                  precioPorKg: (x.precioPorKg as number | null) ?? null,
                });
                return { ...x, ...override };
              });
          }
        } catch {
          toast.error("No se pudo aplicar la lista de precios — se mantiene el precio de catálogo");
          rows = rawCatalogRef.current;
        }
      }
      const rounding = priceRoundingRef.current;
      if (rounding && rounding !== "none") {
        rows = rows.map((x) => ({
          ...x,
          ...roundPriceFields(
            { precioUnitario: (x.precioUnitario as number | null) ?? null, precioPorKg: (x.precioPorKg as number | null) ?? null },
            rounding,
          ),
        }));
      }
      setProductos(
        rows.map((x) => ({
          id: String(x.id),
          nombre: String(x.name ?? x.nombre ?? ""),
          sku: (x.sku as string | null) ?? null,
          ean: (x.ean as string | null) ?? null,
          precioUnitario: (x.precioUnitario as number | null) ?? null,
          precioPorKg: (x.precioPorKg as number | null) ?? null,
          tipoVenta: ((x.tipoVenta as string) === "peso" ? "peso" : "unidad") as "unidad" | "peso",
          unidadMedida: String(x.unidadMedida ?? "kg"),
          minQty: x.minQty != null ? Number(x.minQty) : null,
          qtyStep: x.qtyStep != null ? Number(x.qtyStep) : null,
          imagenUrl: (x.imagenUrl as string | null) ?? null,
          categoriaId: (x.categoriaId as string | null) ?? null,
          categoriaName: x.categoriaName as string | undefined,
          priceMode: x.priceMode === "net" ? "net" : "final",
          taxCategory: (["taxable", "exempt", "non_taxable", "not_subject"] as const).includes(x.taxCategory as any)
            ? (x.taxCategory as "taxable" | "exempt" | "non_taxable" | "not_subject")
            : "taxable",
          taxRate: x.taxRate != null ? Number(x.taxRate) : null,
        })),
      );
      // SCRUM-428: una venta en curso quedaba con precios de la lista
      // anterior "congelados" en el carrito (CartItem.precio es un snapshot
      // tomado al agregar el ítem) — cambiar de lista debe resetear la venta,
      // no solo actualizar el catálogo visible.
      setCart((prev) => {
        if (prev.length === 0) return prev;
        toast.info("Se reinició la venta en curso: cambiaste la lista de precios");
        return [];
      });
    },
    [tid],
  );

  const updateQty = (productId: string, delta: number) => {
    measurePosInteraction("pos-update-qty");
    setCart((prev) => {
      const next = prev.map((i) => {
        if (i.productId !== productId) return i;
        const q = applySaleQtyDelta(i.qty, delta, i);
        return { ...i, qty: q };
      });
      return next.filter((i) => i.qty > 0);
    });
  };

  const removeFromCart = (productId: string) => {
    measurePosInteraction("pos-remove-item");
    setCart((prev) => prev.filter((i) => i.productId !== productId));
  };

  const subtotal = cart.reduce((s, i) => s + i.precio * i.qty, 0);

  // ── SCRUM-359: descuento manual del cajero, sobre el total final ──
  const [manualDiscount, setManualDiscount] = useState<{ type: "percent" | "fixed"; value: number; note: string } | null>(null);
  const manualDiscountAmount = manualDiscount
    ? manualDiscount.type === "percent"
      ? subtotal * (Math.min(100, Math.max(0, manualDiscount.value)) / 100)
      : Math.min(subtotal, Math.max(0, manualDiscount.value))
    : 0;
  const totalConDescuento = Math.max(0, subtotal - manualDiscountAmount);
  // SCRUM-393/507: el breakdown fiscal base se deriva localmente del snapshot
  // fiscal precargado del producto — sin request por mutación del carrito. Se
  // inyecta el descuento manual ya conocido (resta directa) para que modal y
  // ticket mantengan Subtotal → IVA → Descuento → Total consistente. Checkout
  // vuelve a validar fiscalidad y totales en backend antes de confirmar.
  const effectiveTaxTotals: TaxBreakdownTotals | null = taxTotals
    ? {
        ...taxTotals,
        discountAmount: manualDiscountAmount > 0 ? manualDiscountAmount : undefined,
        discountLabel:
          manualDiscount?.type === "percent" ? `Descuento (${manualDiscount.value}%)` : "Descuento",
      }
    : null;

  // ── Modo añadir a pedido: cargar orden destino ──
  useEffect(() => {
    if (!tid || !addToOrderId) {
      setAddTarget(null);
      return;
    }
    let cancelled = false;
    setAddTargetLoading(true);
    apiGetJson<AddTargetOrder>(`/api/orders?id=${encodeURIComponent(addToOrderId)}`, tid)
      .then((o) => {
        if (cancelled) return;
        if (!o?.id) {
          toast.error("Pedido no encontrado");
          void navigate({ search: { addToOrder: undefined } });
          return;
        }
        if (!canAddItemsToOrder({ status: o.status })) {
          toast.error("Ese pedido no admite más productos");
          void navigate({ search: { addToOrder: undefined } });
          return;
        }
        setAddTarget(o);
      })
      .catch(() => {
        if (!cancelled) {
          toast.error("No se pudo cargar el pedido");
          void navigate({ search: { addToOrder: undefined } });
        }
      })
      .finally(() => {
        if (!cancelled) setAddTargetLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tid, addToOrderId, navigate]);

  const exitAddMode = useCallback(() => {
    setCart([]);
    void navigate({ search: { addToOrder: undefined } });
  }, [navigate]);

  const openOrderPicker = useCallback(async () => {
    if (!tid) return;
    setPickOpen(true);
    setPickLoading(true);
    try {
      const list = await apiGetJson<AddTargetOrder[] | { orders?: AddTargetOrder[] }>("/api/orders?limit=80", tid);
      const rows = Array.isArray(list) ? list : (list.orders ?? []);
      setPickOrders(rows.filter((o) => o?.id && canAddItemsToOrder({ status: String(o.status) })));
    } catch {
      toast.error("No se pudieron cargar los pedidos");
      setPickOrders([]);
    } finally {
      setPickLoading(false);
    }
  }, [tid]);

  const enterAddMode = useCallback(
    (orderId: string) => {
      setPickOpen(false);
      setShowPago(false);
      setCart([]);
      void navigate({ search: { addToOrder: orderId } });
    },
    [navigate],
  );

  const totalItems = cart.reduce((s, i) => s + i.qty, 0);


  // ── Confirmar agregados a pedido existente ──
  const handleConfirmAddToOrder = async () => {
    if (!tid || !addToOrderId || cart.length === 0) return;
    setProcesando(true);
    const cartSnapshot = [...cart];
    try {
      const r = await apiSendJson<{
        ok?: boolean;
        error?: string;
        amountDue?: number;
        deltaAmount?: number;
        kitchenBatch?: number;
      }>(
        "/api/orders/items",
        "POST",
        {
          orderId: addToOrderId,
          items: cartSnapshot.map((i) => ({
            productId: i.productId,
            qty: i.qty,
            precioUnitario: i.precio,
            tipoVenta: i.tipoVenta,
            unidadMedida: i.unidadMedida,
            nombre: i.nombre,
            variantId: i.variantId ?? null,
            variantName: i.variantName ?? null,
          })),
        },
        tid,
      );
      if (r.error) {
        toast.error(r.error);
        return;
      }
      const delta = r.deltaAmount ?? cartSnapshot.reduce((s, i) => s + i.precio * i.qty, 0);
      toast.success(`Agregado al pedido (+${pesos(delta)})`);
      if ((r.amountDue ?? 0) >= 0.01) {
        toast.message(`Saldo a cobrar: ${pesos(r.amountDue ?? 0)} — cobralo desde Pedidos`);
      }
      setCart([]);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error al agregar");
    } finally {
      setProcesando(false);
    }
  };

  // ── SCRUM-81: modo operativo (fullscreen + atajos) ──
  const searchInputRef = useRef<HTMLInputElement>(null);
  const triggerCobrar = useCallback(() => {
    if (cart.length === 0 || procesando) return;
    setCartOpen(false);
    if (addMode) void handleConfirmAddToOrder();
    else setShowPago(true);
  }, [cart.length, procesando, addMode]);
  const posShortcuts = useMemo<OperationalShortcut[]>(
    () => [
      {
        id: "focus-search",
        keys: "F2",
        label: "Buscar producto",
        description: "Enfoca el buscador de productos",
        handler: () => {
          searchInputRef.current?.focus();
          searchInputRef.current?.select();
        },
      },
      {
        id: "cobrar",
        keys: "F9",
        label: addMode ? "Confirmar agregados" : "Cobrar",
        description: "Abre el pago con el ticket actual",
        handler: triggerCobrar,
        disabled: cart.length === 0 || procesando,
      },
      {
        id: "toggle-scanner",
        keys: "F3",
        label: "Foco escáner",
        description: "Enfoca captura dedicada (la pistola HID ya funciona sin modo especial)",
        handler: () => {
          setScanMode((m) => (m === "keyboard" ? "none" : "keyboard"));
          searchInputRef.current?.focus();
        },
      },
    ],
    [addMode, triggerCobrar, cart.length, procesando],
  );
  const operationalMode = useOperationalMode({
    surfaceId: "pos.caja",
    tenantId: tid ?? null,
    shortcuts: posShortcuts,
  });

  const refreshPendingOffline = useCallback(async () => {
    if (!tid) return;
    try {
      setPendingOfflineSales(await countPendingOfflineSales(tid));
    } catch {
      /* ignore */
    }
  }, [tid]);

  const runOfflineSync = useCallback(async () => {
    // SCRUM-239: sin la capability no hay replay, ni siquiera de outbox heredado.
    if (!tid || !offlineCapabilityEnabled || !getNavigatorOnline()) return;
    setSyncingOffline(true);
    try {
      const r = await syncOfflinePosSales(tid);
      await refreshPendingOffline();
      if (r.synced > 0) toast.success(`${r.synced} venta${r.synced === 1 ? "" : "s"} sincronizada${r.synced === 1 ? "" : "s"}`);
      if (r.failed > 0) toast.error(`${r.failed} venta${r.failed === 1 ? "" : "s"} no se pudieron sync`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al sincronizar");
    } finally {
      setSyncingOffline(false);
    }
  }, [tid, offlineCapabilityEnabled, refreshPendingOffline]);

  useEffect(() => {
    if (online && tid) void runOfflineSync();
  }, [online, tid, runOfflineSync]);

  // ── Cobrar ──
  const handleCobrar = async (payload: {
    metodo: MetodoPago;
    montoPagado: number;
    vuelto: number;
    codigoGC?: string;
    loyaltyPoints?: number;
    customerId?: string;
    paymentMethods: Array<{ method: string; amount: number }>;
    invoiceMode?: PosInvoiceMode;
    fulfillmentStatus?: OrderStatus;
    manualAck?: boolean;
  }) => {
    if (!tid || !sessionId || cart.length === 0) return;
    setProcesando(true);
    const cartSnapshot = [...cart];
    const {
      metodo,
      montoPagado,
      vuelto,
      codigoGC: gcCode,
      loyaltyPoints,
      customerId,
      paymentMethods,
      invoiceMode: payloadInvoiceMode,
      fulfillmentStatus,
      manualAck,
    } = payload;
    const mode: PosInvoiceMode = payloadInvoiceMode ?? invoiceMode;
    const methods =
      paymentMethods?.length > 0
        ? paymentMethods
        : [{ method: metodo, amount: montoPagado }];
    const clientSaleId = newClientSaleId();
    try {
      // SCRUM-44: sin red → outbox local (solo efectivo, sin loyalty/GC/factura/cliente).
      if (!getNavigatorOnline()) {
        const gate = canQueueOfflineSale({
          online: false,
          offlineCapabilityEnabled,
          paymentMethods: methods,
          hasLoyalty: Boolean(loyaltyPoints && loyaltyPoints > 0),
          hasGiftCard: Boolean(gcCode),
          hasCustomerId: Boolean(customerId || ticketCustomer),
          invoiceMode: mode,
        });
        if (!gate.ok) {
          toast.error(gate.reason);
          return;
        }
        const sale: PosOfflineOutboxSale = {
          id: clientSaleId,
          tenantId: tid,
          sessionId,
          createdAt: new Date().toISOString(),
          status: "pending",
          attempts: 0,
          lastError: null,
          syncedOrderId: null,
          payload: {
            items: cartSnapshot.map((i) => ({
              productId: i.productId,
              qty: i.qty,
              precioUnitario: i.precio,
              nombre: i.nombre,
              variantId: i.variantId ?? null,
              variantName: i.variantName ?? null,
              tipoVenta: i.tipoVenta,
              unidadMedida: i.unidadMedida,
            })),
            paymentMethods: methods,
            changeGiven: vuelto,
            invoiceMode: "none",
            offline: true,
          },
        };
        await enqueueOfflineSale(sale);
        const pending = await countPendingOfflineSales(tid);
        setPendingOfflineSales(pending);
        toast.success("Venta guardada offline — se sincroniza al volver la red");
        setLastSale({
          orderId: `OFFLINE-${clientSaleId.slice(-8)}`,
          total: cartSnapshot.reduce((s, i) => s + i.precio * i.qty, 0),
          metodo,
          vuelto,
          items: cartSnapshot,
          invoiceSummary: "Pendiente de sync",
        });
        setCart([]);
        setManualDiscount(null);
        setShowPago(false);
        setCartOpen(false);
        setTicketCustomer(null);
        setInvoiceMode(defaultPosInvoiceMode(invoicingPolicy));
        if (metodo === "efectivo") {
          void tryHipRoleAction(tid, "cashDrawer", "openDrawer");
        }
        return;
      }

      // HIP de tarjeta se ejecuta solo en PosPagoModal (evitar doble cobro).
      const r = await apiSendJson<{ ok?: boolean; error?: string; orderId?: string; orderNumber?: number | null; total?: number; invoice?: { ok?: boolean; offlineCaea?: boolean; invoice?: { cae?: string | null; comprobanteNro?: string | null } } }>(
        `/api/pos/checkout`,
        "POST",
        {
          sessionId,
          items: cartSnapshot.map((i) => ({
            productId: i.productId,
            qty: i.qty,
            precioUnitario: i.precio,
            tipoVenta: i.tipoVenta,
            unidadMedida: i.unidadMedida,
            nombre: i.nombre,
            variantId: i.variantId ?? null,
            variantName: i.variantName ?? null,
          })),
          paymentMethods: methods,
          giftCardCode: gcCode || undefined,
          loyaltyPoints: loyaltyPoints || undefined,
          customerId: customerId || undefined,
          changeGiven: vuelto,
          invoiceMode: mode,
          fulfillmentStatus,
          manualAck,
          idempotencyKey: clientSaleId,
          manualDiscount: manualDiscount
            ? { type: manualDiscount.type, value: manualDiscount.value, note: manualDiscount.note || undefined }
            : undefined,
        },
        tid,
      );
      if (r.error) {
        toast.error(r.error);
        return;
      }
      const orderId = r.orderId ?? "—";
      const inv = (r as { invoice?: { ok?: boolean; offlineCaea?: boolean; invoice?: { cae?: string | null; comprobanteNro?: string | null } } }).invoice;
      let invoiceSummary: string | null = null;
      if (mode !== "none") {
        if (inv && inv.ok) {
          invoiceSummary = inv.offlineCaea
            ? `Factura CAEA offline ${inv.invoice?.comprobanteNro ?? ""}`.trim()
            : `Facturada${inv.invoice?.comprobanteNro ? ` · ${inv.invoice.comprobanteNro}` : ""}`;
          toast.success(
            inv.offlineCaea
              ? `Venta ${orderId} · factura CAEA offline ${inv.invoice?.comprobanteNro ?? ""}`
              : `¡Venta registrada! Orden ${orderId} · facturada`,
          );
        } else {
          invoiceSummary = "Facturación solicitada (pendiente o no emitida)";
          toast.success(`¡Venta registrada! Orden ${orderId}`);
        }
      } else {
        toast.success(`¡Venta registrada! Orden ${orderId}`);
      }
      setLastSale({
        orderId,
        orderNumber: r.orderNumber ?? null,
        total: r.total ?? cartSnapshot.reduce((s, i) => s + i.precio * i.qty, 0),
        metodo,
        vuelto,
        items: cartSnapshot,
        invoiceSummary,
        customerEmail: ticketCustomer?.email ?? null,
        customerName: ticketCustomer?.name ?? null,
      });
      setCart([]);
      setManualDiscount(null);
      setShowPago(false);
      setCartOpen(false);
      setTicketCustomer(null);
      setInvoiceMode(defaultPosInvoiceMode(invoicingPolicy));
      if (metodo === "efectivo") {
        void tryHipRoleAction(tid, "cashDrawer", "openDrawer");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Error al procesar el cobro";
      toast.error(msg);
    } finally {
      setProcesando(false);
    }
  };

  const printTicket = async (sale: NonNullable<typeof lastSale>) => {
    const escpos = buildReceiptEscPos(
      {
        tenantName: businessInfo.name,
        tenantAddress: businessInfo.address,
        tenantPhone: businessInfo.phone,
        tenantWhatsapp: businessInfo.whatsapp,
        tenantWebsite: businessInfo.website,
        orderId: sale.orderId,
        orderNumber: sale.orderNumber ?? null,
        items: sale.items.map((i) => ({
          name: i.nombre,
          qty: i.qty,
          price: i.precio,
          total: i.precio * i.qty,
        })),
        total: sale.total,
        paymentMethod: sale.metodo,
        paymentStatus: "paid",
      },
      paperWidthChars(getPrinterForRole("cashier_printer")?.paperWidth ?? "80mm"),
    );

    setPrinting(true);
    try {
      const printer = getPrinterForRole("cashier_printer");
      if (printer?.printerName) {
        try {
          if (!qzReady) await qzConnect();
          if (window.qz?.websocket.isActive()) {
            await qzPrint(printer.printerName, [{ type: "raw", format: "plain", data: escpos }], printer.copies ?? 1);
            toast.success("Ticket impreso (QZ Tray)");
            return;
          }
        } catch {
          /* fallback HIP abajo */
        }
      }

      const hip = await tryHipRoleAction(tid!, "posPrinter", "print", {
        content: escpos,
        kind: "receipt",
      });
      if (hip.mode === "hardware") {
        toast.success("Ticket enviado a impresora HIP");
        return;
      }
      toast.error(
        "Sin impresora. Configurá QZ en Ajustes → Impresoras o asigná impresora en Hardware → Asignaciones.",
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al imprimir el ticket");
    } finally {
      setPrinting(false);
    }
  };

  // ── Filtros ──
  const productosFiltrados = productos.filter((p) => {
    const matchCat = catActiva === "all" || p.categoriaId === catActiva;
    const q = search.toLowerCase();
    const matchSearch =
      !search ||
      p.nombre.toLowerCase().includes(q) ||
      (p.sku ?? "").toLowerCase().includes(q) ||
      (p.ean ?? "").toLowerCase().includes(q);
    return matchCat && matchSearch;
  });

  // ── Orden alfabético + índice de letras (compartido por Tarjetas y Lista) ──
  const productosOrdenados = useMemo(
    () => [...productosFiltrados].sort((a, b) => a.nombre.localeCompare(b.nombre, "es", { sensitivity: "base" })),
    [productosFiltrados],
  );
  const letrasDisponibles = useMemo(
    () => new Set(productosOrdenados.map((p) => posFirstLetter(p.nombre))),
    [productosOrdenados],
  );
  const primerProductoPorLetra = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of productosOrdenados) {
      const letra = posFirstLetter(p.nombre);
      if (!map.has(letra)) map.set(letra, p.id);
    }
    return map;
  }, [productosOrdenados]);
  const productListRef = useRef<HTMLDivElement>(null);
  const scrollToLetter = useCallback(
    (letra: string) => {
      const productId = primerProductoPorLetra.get(letra);
      if (!productId) return;
      const el = productListRef.current?.querySelector(`[data-product-id="${productId}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [primerProductoPorLetra],
  );

  // ── Render ──
  if (loading) return <PageLoader />;
  if (lock) return <FeatureLockedState payload={lock} />;
  if (!sessionId)
    return (
      <div className="flex flex-col items-center gap-4 py-20">
        <p className="text-muted-foreground">No hay sesión de caja activa.</p>
        <Button asChild>
          <a href="/app/pos">Abrir sesión</a>
        </Button>
      </div>
    );

  return (
    <OperationalModeShell
      mode={operationalMode}
      className={OPERATIONAL_HOST_LAYOUT}
    >
      <PosConnectivityBanner
        status={syncingOffline ? "syncing" : online ? "online" : "offline"}
        pendingCount={pendingOfflineSales}
        offlineEnabled={offlineCapabilityEnabled}
        syncing={syncingOffline}
        onSyncNow={() => void runOfflineSync()}
      />
      {/* Modo añadir a pedido / acceso desde caja */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border/40 bg-card px-3 py-2">
        {addMode ? (
          <>
            <div className="min-w-0 flex items-center gap-2 text-sm">
              <PackagePlus className="h-4 w-4 shrink-0 text-primary" />
              <span className="truncate font-medium">
                {addTargetLoading
                  ? "Cargando pedido…"
                  : addTarget
                    ? `Añadiendo a #${shortOrderId(addTarget.id)} · ${addTarget.customerName || "Cliente"}`
                    : "Añadir a pedido"}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {productFlags.hardware_customer_display ? (
                <CustomerDisplayPairPanel
                  tenantId={tid!}
                  cashSessionId={sessionId}
                  enabled
                />
              ) : null}
              <OperationalModeToggle mode={operationalMode} />
              <Button type="button" size="sm" variant="outline" onClick={exitAddMode}>
                Volver a venta
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">Venta nueva</p>
            <div className="flex shrink-0 items-center gap-2">
              {productFlags.price_lists && priceListOptions.length > 0 ? (
                <Select
                  value={selectedPriceListId ?? "__catalogo__"}
                  onValueChange={(v) => void applyPriceListSelection(v === "__catalogo__" ? null : v)}
                >
                  <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="pos-price-list-select">
                    <SelectValue placeholder="Lista de precios" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__catalogo__">Catálogo</SelectItem>
                    {priceListOptions.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
              {productFlags.hardware_customer_display ? (
                <CustomerDisplayPairPanel
                  tenantId={tid!}
                  cashSessionId={sessionId}
                  enabled
                />
              ) : null}
              <OperationalModeToggle mode={operationalMode} />
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="gap-1.5"
                onClick={() => void openOrderPicker()}
              >
                <ClipboardList className="h-4 w-4" />
                Añadir a pedido
              </Button>
            </div>
          </>
        )}
      </div>

      {/* ── SCANNER INPUT OCULTO (keyboard mode) ── */}
      {scanMode === "keyboard" && (
        <input
          ref={scanRef}
          className="pointer-events-none fixed -top-full opacity-0"
          value={scanBuffer}
          onChange={(e) => setScanBuffer(e.target.value)}
          onKeyDown={handleScanInput}
          autoFocus
        />
      )}

      {/* ── MODAL CÁMARA ── */}
      {scanMode === "camera" && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black">
          <video
            ref={cameraRef}
            autoPlay
            playsInline
            className="h-full max-h-[60vh] w-full max-w-sm rounded-xl object-cover"
          />
          <p className="mt-4 text-sm text-white/70">Apuntá al código de barras</p>
          <Button className="mt-3" variant="outline" onClick={stopCamera}>
            <X className="mr-2 h-4 w-4" /> Cancelar
          </Button>
        </div>
      )}



      <PosEditProductDialog
        open={Boolean(editProduct)}
        tenantId={tid ?? ""}
        product={
          editProduct
            ? {
                productId: editProduct.productId,
                nombre: editProduct.nombre,
                precio: editProduct.precio,
                tipoVenta: editProduct.tipoVenta,
                qty: editProduct.qty,
                unidadMedida: editProduct.unidadMedida,
                minQty: editProduct.minQty,
                qtyStep: editProduct.qtyStep,
              }
            : null
        }
        onClose={() => setEditProduct(null)}
        onSaved={(next) => {
          if (!editProduct) return;
          const id = editProduct.productId;
          // SCRUM-430: esta edición afecta solo la venta en curso — el
          // producto original del catálogo (`productos`) NO se toca acá.
          setCart((prev) =>
            prev.map((i) =>
              i.productId === id
                ? {
                    ...i,
                    precio: next.precio,
                    ...(next.qty != null ? { qty: next.qty } : {}),
                  }
                : i,
            ),
          );
          setHighlightProductId(id);
          window.setTimeout(() => setHighlightProductId((cur) => (cur === id ? null : cur)), 1800);
        }}
      />

      {/* ── MODAL PAGO (solo venta nueva) ── */}
      {showPago && !addMode && (
      <PosPagoModal
          total={totalConDescuento}
          taxTotals={effectiveTaxTotals}
          tenantId={tid!}
          flags={productFlags}
          paymentTerminalBound={paymentTerminalBound}
          procesando={procesando}
          cashSessionId={sessionId}
          invoiceMode={invoiceMode}
          onInvoiceModeChange={setInvoiceMode}
          invoicingPolicy={invoicingPolicy}
          showInvoicing
          preselectedCustomer={
            ticketCustomer
              ? {
                  id: ticketCustomer.id,
                  name: ticketCustomer.name,
                  email: ticketCustomer.email,
                  phone: ticketCustomer.phone,
                  dni: ticketCustomer.dni,
                  loyaltyBalance: ticketCustomer.loyaltyBalance,
                  giftCardBalance: ticketCustomer.giftCardBalance,
                  currentAccountEnabled: ticketCustomer.currentAccountEnabled,
                  currentAccountAvailable: ticketCustomer.currentAccountAvailable,
                }
              : null
          }
          onClose={() => setShowPago(false)}
          onConfirm={(p) => {
            void handleCobrar(p);
          }}
        />
      )}

      {/* Picker: pedidos abiertos para modo añadir */}
      <Dialog open={pickOpen} onOpenChange={setPickOpen}>
        <DialogContent className="max-h-[80vh] max-w-md overflow-hidden flex flex-col gap-0 p-0">
          <DialogHeader className="border-b px-4 py-3">
            <DialogTitle>Añadir a un pedido</DialogTitle>
            <p className="text-xs text-muted-foreground">
              Usá la grilla de caja para sumar productos a una orden abierta (POS u online).
            </p>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
            {pickLoading ? (
              <p className="py-8 text-center text-sm text-muted-foreground">Cargando…</p>
            ) : pickOrders.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">No hay pedidos que admitan agregados</p>
            ) : (
              <ul className="space-y-1">
                {pickOrders.map((o) => (
                  <li key={o.id}>
                    <button
                      type="button"
                      className="flex w-full flex-col rounded-md px-3 py-2.5 text-left hover:bg-muted/60"
                      onClick={() => enterAddMode(o.id)}
                    >
                      <span className="text-sm font-medium">
                        #{shortOrderId(o.id)} · {o.customerName || "Cliente"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {o.status} · {pesos(Number(o.precioFinal ?? o.precioEstimado ?? 0))}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {lastSale && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 sm:items-center">
          <div className="relative w-full max-w-sm rounded-2xl border border-border/60 bg-card p-5 space-y-4">
            <button
              type="button"
              onClick={() => setLastSale(null)}
              aria-label="Cerrar"
              className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="text-center space-y-1">
              <CheckCircle2 className="mx-auto h-10 w-10 text-green-400" />
              <h2 className="text-base font-semibold">Venta registrada</h2>
              <p className="text-xs text-muted-foreground font-mono">{lastSale.orderId}</p>
              <p className="text-2xl font-bold tabular-nums text-primary">{pesos(lastSale.total)}</p>
              {lastSale.vuelto > 0 && (
                <p className="text-sm text-green-400">Vuelto {pesos(lastSale.vuelto)}</p>
              )}
            </div>

            {/*
              SCRUM-340: la facturación vive acá, después del cobro. "Nueva venta"
              queda siempre disponible abajo: emitir, imprimir o enviar nunca
              bloquea el arranque de la próxima venta.
            */}
            {/* La venta offline todavía no tiene pedido en el servidor: no hay
                nada que facturar hasta que sincronice. */}
            {tid && invoicingEnabled && !lastSale.orderId.startsWith("OFFLINE-") ? (
              <PosSaleInvoicePanel
                tenantId={tid}
                orderId={lastSale.orderId}
                policy={invoicingPolicy}
                initialSummary={lastSale.invoiceSummary}
                customerEmail={lastSale.customerEmail}
                customerName={lastSale.customerName}
                onPrint={() => void printTicket(lastSale)}
              />
            ) : lastSale.invoiceSummary ? (
              <p className="text-xs text-muted-foreground" data-testid="pos-sale-invoice-summary">
                {lastSale.invoiceSummary}
              </p>
            ) : null}

            <div className="flex gap-2">
              <Button
                className="flex-1"
                variant="outline"
                disabled={printing}
                onClick={() => void printTicket(lastSale)}
              >
                {printing ? "Imprimiendo…" : "Imprimir ticket"}
              </Button>
              <Button className="flex-1" onClick={() => setLastSale(null)}>
                Nueva venta
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── LAYOUT PRINCIPAL 2 COLUMNAS ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* COLUMNA IZQUIERDA: Catálogo */}
        <div className="flex flex-1 flex-col overflow-hidden border-r border-border/40">
          {/* Buscador + scanner */}
          <div className="flex gap-2 border-b border-border/40 p-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                ref={searchInputRef}
                data-testid="pos-product-search"
                data-pos-scan-capture="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  const code = search.trim();
                  if (!looksLikeScanCode(code)) return;
                  e.preventDefault();
                  applyScannedCode(code);
                }}
                placeholder="Buscar o escanear EAN/SKU…"
                className="h-11 w-full rounded-xl border border-border/60 bg-card pl-9 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary/40"
                autoComplete="off"
              />
            </div>
            {/* Pistola física */}
            <button
              onClick={() => setScanMode((m) => (m === "keyboard" ? "none" : "keyboard"))}
              title="Escáner pistola (Enter)"
              className={`flex h-11 min-w-[44px] items-center justify-center rounded-xl border text-sm transition-colors
                ${scanMode === "keyboard" ? "border-primary bg-primary/20 text-primary" : "border-border/60 bg-card text-muted-foreground hover:text-foreground"}`}
            >
              <ScanBarcode className="h-5 w-5" />
            </button>
            {/* Escáner HIP (si hay binding; si no, el botón sigue y cae a manual) */}
            <button
              onClick={() => void hipScanToCart()}
              disabled={hipScanning}
              title="Leer con escáner HIP asignado"
              className="flex h-11 min-w-[44px] items-center justify-center rounded-xl border border-border/60 bg-card text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
            >
              <QrCode className="h-5 w-5" />
            </button>
            {/* Cámara */}
            <button
              onClick={startCamera}
              title="Escanear con cámara"
              className="flex h-11 min-w-[44px] items-center justify-center rounded-xl border border-border/60 bg-card text-muted-foreground transition-colors hover:text-foreground"
            >
              <Camera className="h-5 w-5" />
            </button>
            {/* Cajón monedero (HIP) — visible si hay asignación; sin ella el flujo es manual */}
            {cashDrawerBound && (
              <button
                onClick={() => void openCashDrawer()}
                disabled={openingDrawer}
                title="Abrir cajón"
                className="flex h-11 min-w-[44px] items-center justify-center rounded-xl border border-border/60 bg-card text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
              >
                <Wallet className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Tabs categorías */}
          {categorias.length > 0 && (
            <div className="flex gap-1.5 overflow-x-auto border-b border-border/40 px-3 py-2">
              <button
                onClick={() => setCatActiva("all")}
                className={`flex-shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors
                  ${catActiva === "all" ? "border-primary bg-primary/10 text-primary" : "border-border/60 text-muted-foreground hover:text-foreground"}`}
              >
                Todos
              </button>
              {categorias.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCatActiva(c.id)}
                  className={`flex-shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors
                    ${catActiva === c.id ? "border-primary bg-primary/10 text-primary" : "border-border/60 text-muted-foreground hover:text-foreground"}`}
                >
                  {c.nombre}
                </button>
              ))}
            </div>
          )}

          {/* Barra: conteo + selector Tarjetas/Lista */}
          <div className="flex items-center justify-between border-b border-border/40 px-3 py-1.5">
            <p className="text-xs text-muted-foreground">
              {productosFiltrados.length} {productosFiltrados.length === 1 ? "producto" : "productos"}
            </p>
            <div
              className="flex items-center rounded-full border border-border/60 p-0.5"
              role="group"
              aria-label="Vista del listado de productos"
            >
              {(["tarjetas", "lista"] as const).map((v) => {
                const Icon = v === "tarjetas" ? LayoutGrid : List;
                const active = vista === v;
                return (
                  <button
                    key={v}
                    type="button"
                    data-testid={`pos-view-${v}`}
                    onClick={() => setVista(v)}
                    aria-pressed={active}
                    aria-label={v === "tarjetas" ? "Ver en tarjetas" : "Ver en lista"}
                    className={`flex h-7 w-8 items-center justify-center rounded-full transition-colors
                      ${active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Grilla / Lista de productos + índice alfabético */}
          <div className="relative flex-1 overflow-hidden">
          <div ref={productListRef} className="h-full overflow-y-auto p-3 pr-6">
            {productosFiltrados.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">
                {search ? "Sin resultados para tu búsqueda." : "No hay productos en esta categoría."}
              </p>
            ) : vista === "lista" ? (
              <div className="flex flex-col gap-1.5">
                {productosOrdenados.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid="pos-product-row"
                    data-sku={p.sku ?? undefined}
                    data-product-id={p.id}
                    onClick={() => addToCart(p)}
                    disabled={unitPrice(p) <= 0 || p.sellableAtLocation === false}
                    className="group flex items-center gap-3 overflow-hidden rounded-xl border border-border/60 bg-card px-2.5 py-2 text-left transition-all hover:border-primary/40 hover:bg-primary/5 disabled:opacity-40"
                  >
                    <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg bg-card">
                      {p.imagenUrl ? (
                        <img src={p.imagenUrl} alt={p.nombre} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full items-center justify-center text-lg text-muted-foreground/20">
                          🛒
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium leading-tight">{p.nombre}</p>
                      {posProductMetaLine(p) ? (
                        <p
                          className="truncate text-[10px] text-muted-foreground"
                          data-testid="pos-product-meta"
                        >
                          {posProductMetaLine(p)}
                        </p>
                      ) : null}
                    </div>
                    <p className="shrink-0 text-sm font-bold tabular-nums text-primary">
                      {unitPrice(p) > 0 ? `${pesos(unitPrice(p))}${p.tipoVenta === "peso" ? `/${p.unidadMedida}` : ""}` : "S/P"}
                    </p>
                  </button>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                {productosOrdenados.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid="pos-product-card"
                    data-sku={p.sku ?? undefined}
                    data-product-id={p.id}
                    onClick={() => addToCart(p)}
                    disabled={unitPrice(p) <= 0 || p.sellableAtLocation === false}
                    className="group flex flex-col overflow-hidden rounded-xl border border-border/60 bg-card text-left transition-all hover:border-primary/40 hover:bg-primary/5 disabled:opacity-40"
                  >
                    {/* Imagen */}
                    <div className="relative h-20 w-full overflow-hidden bg-card">
                      {p.imagenUrl ? (
                        <img src={p.imagenUrl} alt={p.nombre} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full items-center justify-center text-2xl text-muted-foreground/20">
                          🛒
                        </div>
                      )}
                    </div>
                    {/* Info */}
                    <div className="flex flex-1 flex-col p-2">
                      <p className="line-clamp-2 text-xs font-medium leading-tight">{p.nombre}</p>
                      {posProductMetaLine(p) ? (
                        <p
                          className="mt-0.5 line-clamp-1 text-[10px] text-muted-foreground"
                          data-testid="pos-product-meta"
                        >
                          {posProductMetaLine(p)}
                        </p>
                      ) : null}
                      <p className="mt-auto pt-1.5 text-sm font-bold tabular-nums text-primary">
                        {unitPrice(p) > 0 ? `${pesos(unitPrice(p))}${p.tipoVenta === "peso" ? `/${p.unidadMedida}` : ""}` : "S/P"}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {letrasDisponibles.size > 0 && (
            <div
              className="absolute inset-y-0 right-0 flex flex-col items-center justify-center gap-px bg-gradient-to-l from-card/90 to-transparent px-0.5"
              role="group"
              aria-label="Saltar a letra"
            >
              {POS_ALPHABET.map((letra) => {
                const habilitada = letrasDisponibles.has(letra);
                return (
                  <button
                    key={letra}
                    type="button"
                    disabled={!habilitada}
                    onClick={() => scrollToLetter(letra)}
                    aria-label={`Ir a productos con ${letra}`}
                    className={`flex h-3 w-4 items-center justify-center rounded-sm text-[8px] font-medium leading-none transition-colors
                      ${habilitada ? "text-muted-foreground hover:bg-primary/15 hover:text-primary" : "text-muted-foreground/20"}`}
                  >
                    {letra}
                  </button>
                );
              })}
            </div>
          )}
          </div>
        </div>

        {/* COLUMNA DERECHA: Ticket — oculta en portrait, visible en landscape */}
        <div className="hidden w-[340px] flex-col lg:flex xl:w-[380px]">
          <TicketPanel
            taxTotals={effectiveTaxTotals}
            cart={cart}
            subtotal={subtotal}
            manualDiscount={manualDiscount}
            manualDiscountAmount={manualDiscountAmount}
            total={totalConDescuento}
            onSetManualDiscount={setManualDiscount}
            onUpdateQty={updateQty}
            onRemove={removeFromCart}
            onEditProduct={setEditProduct}
            highlightProductId={highlightProductId}
            ticketScrollRef={ticketScrollRef}
            onCobrar={() => (addMode ? void handleConfirmAddToOrder() : setShowPago(true))}
            procesando={procesando}
            addMode={addMode}
            ticketCustomer={addMode ? null : ticketCustomer}
            onTicketCustomerChange={addMode ? undefined : setTicketCustomer}
            tenantId={tid ?? null}
            addOrderLabel={
              addTarget
                ? `#${shortOrderId(addTarget.id)} · ${addTarget.customerName || "Cliente"}`
                : addTargetLoading
                  ? "Cargando pedido…"
                  : null
            }
          />
        </div>
      </div>

      {/* ── PORTRAIT: Botón flotante del carrito ── */}
      <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden">
        {cartOpen && (
          <div className="max-h-[70vh] overflow-y-auto rounded-t-2xl border-t border-border/60 bg-card shadow-2xl">
            <TicketPanel
              cart={cart}
              subtotal={subtotal}
              taxTotals={effectiveTaxTotals}
              manualDiscount={manualDiscount}
              manualDiscountAmount={manualDiscountAmount}
              total={totalConDescuento}
              onSetManualDiscount={setManualDiscount}
              onUpdateQty={updateQty}
              onRemove={removeFromCart}
              onEditProduct={setEditProduct}
              highlightProductId={highlightProductId}
              ticketScrollRef={ticketScrollRef}
              onCobrar={() => {
                setCartOpen(false);
                if (addMode) void handleConfirmAddToOrder();
                else setShowPago(true);
              }}
              procesando={procesando}
              compact
              addMode={addMode}
              ticketCustomer={addMode ? null : ticketCustomer}
              onTicketCustomerChange={addMode ? undefined : setTicketCustomer}
              tenantId={tid ?? null}
              addOrderLabel={
                addTarget
                  ? `#${shortOrderId(addTarget.id)} · ${addTarget.customerName || "Cliente"}`
                  : addTargetLoading
                    ? "Cargando pedido…"
                    : null
              }
            />
          </div>
        )}
        <button
          onClick={() => setCartOpen((v) => !v)}
          className="flex w-full items-center justify-between border-t border-border/60 bg-card px-4 py-3"
        >
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-primary" />
            <span className="text-sm font-medium">
              {totalItems > 0 ? `${totalItems} ítem${totalItems !== 1 ? "s" : ""}` : "Carrito vacío"}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold tabular-nums text-primary">{pesos(totalConDescuento)}</span>
            <ChevronUp className={`h-4 w-4 transition-transform ${cartOpen ? "rotate-180" : ""}`} />
          </div>
        </button>
      </div>
      <PosCajaTour />
    </OperationalModeShell>
  );
}

// ─── TICKET PANEL (reutilizable en sidebar y bottom-sheet) ────────────────────

function TicketPanel({
  cart,
  subtotal,
  taxTotals,
  onUpdateQty,
  onRemove,
  onEditProduct,
  highlightProductId,
  ticketScrollRef,
  onCobrar,
  procesando,
  compact,
  addMode,
  addOrderLabel,
  ticketCustomer,
  onTicketCustomerChange,
  tenantId,
  manualDiscount,
  manualDiscountAmount,
  total,
  onSetManualDiscount,
}: {
  cart: CartItem[];
  subtotal: number;
  taxTotals?: TaxBreakdownTotals | null;
  onUpdateQty: (id: string, delta: number) => void;
  onRemove: (id: string) => void;
  onEditProduct?: (item: CartItem) => void;
  highlightProductId?: string | null;
  ticketScrollRef?: RefObject<HTMLDivElement | null>;
  onCobrar: () => void;
  procesando: boolean;
  compact?: boolean;
  addMode?: boolean;
  addOrderLabel?: string | null;
  ticketCustomer?: PosTicketCustomer | null;
  onTicketCustomerChange?: (c: PosTicketCustomer | null) => void;
  tenantId?: string | null;
  /** SCRUM-359: descuento manual del cajero sobre el total final. */
  manualDiscount?: { type: "percent" | "fixed"; value: number; note: string } | null;
  manualDiscountAmount?: number;
  total?: number;
  onSetManualDiscount?: (d: { type: "percent" | "fixed"; value: number; note: string } | null) => void;
}) {
  return (
    <div className="flex h-full flex-col">
      <div className={`border-b border-border/40 px-4 py-3 ${compact ? "" : ""}`}>
        <p className="text-sm font-semibold">{addMode ? "Agregar al pedido" : "Ticket"}</p>
        {addMode && addOrderLabel ? (
          <p className="text-xs text-primary">{addOrderLabel}</p>
        ) : null}
        {cart.length > 0 && (
          <p className="text-xs text-muted-foreground">{cart.reduce((s, i) => s + i.qty, 0)} productos</p>
        )}
      </div>

      {/* Items */}
      <div ref={ticketScrollRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
        {cart.length === 0 ? (
          <p className="py-8 text-center text-xs text-muted-foreground">{addMode ? "Elegí productos de la grilla para sumar al pedido" : "Agregá productos desde el catálogo"}</p>
        ) : (
          cart.map((item) => (
            <div
              key={item.productId}
              data-ticket-product-id={item.productId}
              data-testid="pos-ticket-line"
              className={`flex flex-col gap-1.5 rounded-xl border px-2 py-2.5 transition-colors ${
                highlightProductId === item.productId
                  ? "border-primary bg-primary/10 ring-1 ring-primary/40"
                  : "border-border/40 bg-card"
              }`}
            >
              {/* Nombre completo, siempre visible, sin truncar */}
              <p className="break-words text-xs font-medium leading-snug" data-testid="pos-ticket-line-name">
                {item.nombre}
              </p>
              <div className="flex items-center gap-2">
                <p className="flex-1 min-w-0 text-xs tabular-nums text-muted-foreground">{pesos(item.precio)}</p>
                {onEditProduct ? (
                  <button
                    type="button"
                    data-testid="pos-ticket-edit-product"
                    aria-label="Editar producto"
                    onClick={() => onEditProduct(item)}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border/60 text-muted-foreground hover:border-primary/40 hover:text-primary"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                ) : null}
                {/* Controles qty */}
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    onClick={() => onUpdateQty(item.productId, -1)}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-border/60 text-muted-foreground hover:border-red-500/40 hover:text-red-400"
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="min-w-8 text-center text-sm font-semibold tabular-nums">{formatSaleQty(item.qty, item)}</span>
                  <button
                    onClick={() => onUpdateQty(item.productId, 1)}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-border/60 text-muted-foreground hover:border-green-500/40 hover:text-green-400"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
                {/* Subtotal + eliminar */}
                <div className="flex shrink-0 items-center gap-1">
                  <p className="w-16 text-right text-xs font-bold tabular-nums">
                    {pesos(item.precio * item.qty)}
                  </p>
                  <button
                    onClick={() => onRemove(item.productId)}
                    className="text-muted-foreground hover:text-red-400"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Totales + cobrar */}
      <div className="border-t border-border/40 p-4 space-y-3">
        {taxTotals ? (
          <TaxBreakdown
            totals={taxTotals}
            compact
            formatMoney={pesos}
          />
        ) : (
          <>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Subtotal</span>
              <span className="text-sm tabular-nums">{pesos(subtotal)}</span>
            </div>
            {!!manualDiscountAmount && manualDiscountAmount > 0 && (
              <div className="flex items-center justify-between text-sm text-emerald-500" data-testid="pos-manual-discount-line">
                <span>
                  Descuento {manualDiscount?.type === "percent" ? `(${manualDiscount.value}%)` : ""}
                </span>
                <span className="tabular-nums">-{pesos(manualDiscountAmount)}</span>
              </div>
            )}
            <div className="flex items-center justify-between font-bold">
              <span>Total</span>
              <span className="text-xl tabular-nums text-primary">{pesos(total ?? subtotal)}</span>
            </div>
          </>
        )}
        {!addMode && onSetManualDiscount ? (
          <PosManualDiscountButton
            value={manualDiscount ?? null}
            onChange={onSetManualDiscount}
            disabled={procesando || cart.length === 0}
          />
        ) : null}
        {!addMode && tenantId && onTicketCustomerChange ? (
          <PosTicketCustomerBar
            tenantId={tenantId}
            customer={ticketCustomer ?? null}
            onChange={onTicketCustomerChange}
            disabled={procesando}
          />
        ) : null}
        <Button
          className="h-14 w-full text-base font-bold"
          disabled={cart.length === 0 || procesando}
          onClick={onCobrar}
        >
          {procesando
            ? "Procesando…"
            : addMode
              ? `Confirmar agregados ${pesos(subtotal)}`
              : `Cobrar ${pesos(total ?? subtotal)}`}
        </Button>
      </div>
    </div>
  );
}

// ─── DESCUENTO MANUAL (SCRUM-359) ──────────────────────────────────────────

type ManualDiscountValue = { type: "percent" | "fixed"; value: number; note: string };

function PosManualDiscountButton({
  value,
  onChange,
  disabled,
}: {
  value: ManualDiscountValue | null;
  onChange: (d: ManualDiscountValue | null) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<"percent" | "fixed">(value?.type ?? "percent");
  const [amountStr, setAmountStr] = useState(value ? String(value.value) : "");
  const [note, setNote] = useState(value?.note ?? "");

  const openModal = () => {
    setType(value?.type ?? "percent");
    setAmountStr(value ? String(value.value) : "");
    setNote(value?.note ?? "");
    setOpen(true);
  };

  const apply = () => {
    const n = Number(amountStr.replace(",", "."));
    if (!Number.isFinite(n) || n <= 0) {
      toast.message("Ingresá un valor de descuento válido");
      return;
    }
    onChange({ type, value: n, note: note.trim() });
    setOpen(false);
  };

  const quitar = () => {
    onChange(null);
    setOpen(false);
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full gap-1.5"
        disabled={disabled}
        onClick={openModal}
        data-testid="pos-open-manual-discount"
      >
        <Tag className="h-3.5 w-3.5" />
        {value ? "Editar descuento" : "Aplicar descuento"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="pos-manual-discount-dialog">
          <DialogHeader>
            <DialogTitle>Descuento manual</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-1">
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={type === "percent" ? "default" : "outline"}
                onClick={() => setType("percent")}
                data-testid="pos-discount-type-percent"
              >
                Porcentaje (%)
              </Button>
              <Button
                type="button"
                variant={type === "fixed" ? "default" : "outline"}
                onClick={() => setType("fixed")}
                data-testid="pos-discount-type-fixed"
              >
                Monto fijo ($)
              </Button>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="pos-discount-amount">{type === "percent" ? "Porcentaje" : "Monto"}</Label>
              <Input
                id="pos-discount-amount"
                inputMode="decimal"
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                placeholder={type === "percent" ? "10" : "1000"}
                autoFocus
                data-testid="pos-discount-amount"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="pos-discount-note">
                Nota <span className="text-xs font-normal text-muted-foreground">(opcional)</span>
              </Label>
              <Input
                id="pos-discount-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Motivo del descuento"
                data-testid="pos-discount-note"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            {value ? (
              <Button type="button" variant="ghost" onClick={quitar} data-testid="pos-discount-remove">
                Quitar descuento
              </Button>
            ) : null}
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={apply} data-testid="pos-discount-apply">
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
